import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function NorfolkCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Norfolk Pothole Claims | AMBER on Every DfT Scorecard | Fixtyer"
        description="Norfolk County Council scored AMBER on all four DfT scorecards — no GREEN anywhere. RED-rated roads hit a five-year high in every road class in 2024, and half the 9,944km network is surveyed only once every four years. See what that means for your pothole claim."
        canonical="/councils/norfolk"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Norfolk', url: '/councils/norfolk' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Norfolk is AMBER on all four DfT scorecards — what does that mean for my claim?',
            answer: "Norfolk received AMBER for Overall, Condition, Spend and Best Practice in the DfT's 2025/26 local road maintenance ratings — no GREEN anywhere. AMBER across the board means the DfT sees room for improvement in every area it measures. For your claim, the most relevant facts sit underneath the ratings: RED-rated roads hit a five-year high in every road class in 2024, and half the network is condition-surveyed only once every four years.",
          },
          {
            question: 'What if my pothole was on a C road or an unclassified road?',
            answer: "That is where Norfolk's survey regime is thinnest. The council's own report says it surveys 25% of U roads each year, so all U roads are surveyed over a four-year period — and C roads are only covered in both directions every four years. U roads alone are 5,025.83km, just over half the network. A defect on one of these roads can sit a long time between condition surveys, which makes prior reports and photographic evidence decisive.",
          },
          {
            question: "Norfolk spends more than its DfT allocation — doesn't that sink my claim?",
            answer: "No. Norfolk's capital spend has exceeded its DfT allocation in each of the last five reported years, yet the DfT still rated its Spend scorecard AMBER. More importantly, Section 58 of the Highways Act 1980 is not a budget defence — the council must show the specific stretch of road that damaged your vehicle was reasonably inspected and maintained. Aggregate spending does not answer that question.",
          },
          {
            question: "Over 95% of Norfolk's spend is preventative — does that hurt my claim?",
            answer: "Not necessarily. The preventative-first approach means just 4.3-6.8% of spend goes on reactive repairs, and in 2024/25 the dominant treatment was surface dressing — 474.56km of it, against only 4.26km of conventional resurfacing across a 9,943.79km network. Surface dressing seals roads in fair condition; it does not rebuild roads already rated RED. With RED percentages at a five-year high in every class, the question for Section 58 is what was done about the roads the council's own data flags as needing maintenance.",
          },
          {
            question: 'How many potholes does Norfolk actually fix?',
            answer: "Norfolk's report records 37,415 potholes filled over five years: 5,516 in 2020/21, 8,244 in 2021/22, 6,589 in 2022/23, 9,246 in 2023/24 and 7,820 in 2024/25. The council says extreme weather drives the spikes. The fluctuating numbers — up 40% in 2023/24, down 15% the next year — support what the report itself acknowledges: pothole numbers surge after hard winters, faster than a fixed inspection cycle anticipates.",
          },
        ]}
      />
      <ServiceSchema 
        councilName="Norfolk County Council" 
        councilSlug="norfolk" 
        rating="amber" 
      />
      <SpeakableSchema
        url="/councils/norfolk"
        headline="Norfolk Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Norfolk</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Norfolk: AMBER on Every Scorecard, No GREEN Anywhere
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Norfolk County Council runs one of the longest road networks in England — 9,943.79km — 
            and the DfT rated it <strong className="text-amber-400">AMBER on all four scorecards</strong>: 
            overall, condition, spend and best practice. The council's own 2025 transparency report shows 
            RED-rated roads at a <strong className="text-rose-400">five-year high in every road class</strong>, 
            while half the network is condition-surveyed only once every four years.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">5,025km</div>
            <div className="text-white font-medium mt-2">Of unclassified roads — surveyed once every four years</div>
            <div className="text-brand-300 text-sm mt-1">
              U roads make up just over half of Norfolk's network. The council surveys 25% of them 
              each year, so any given U road waits four years between condition surveys.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Norfolk's own transparency report — in 2024, RED-rated 
            roads reached their highest level of the series in every single road class
          </p>

          {/* A-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">A roads (819.68km — 8.2% of network): RED up 50% in a year</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">34%</td>
                    <td className="px-4 py-2 text-center">62%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">34%</td>
                    <td className="px-4 py-2 text-center">62%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">36%</td>
                    <td className="px-4 py-2 text-center">60%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">33%</td>
                    <td className="px-4 py-2 text-center">63%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6%</td>
                    <td className="px-4 py-2 text-center font-bold">30%</td>
                    <td className="px-4 py-2 text-center font-bold">64%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              After four flat years at 4%, RED-rated A roads jumped to 6% in 2024 — a 50% rise in a 
              single year on the county's most heavily trafficked routes. That is roughly 49km of 
              main road that the council's own indicator says "should be considered for maintenance".
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (4,072.50km — 41% of network): RED at a five-year high</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">31%</td>
                    <td className="px-4 py-2 text-center">64%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">30%</td>
                    <td className="px-4 py-2 text-center">64%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">29%</td>
                    <td className="px-4 py-2 text-center">66%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">28%</td>
                    <td className="px-4 py-2 text-center">66%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7%</td>
                    <td className="px-4 py-2 text-center font-bold">26%</td>
                    <td className="px-4 py-2 text-center font-bold">67%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED rose to 7% in 2024 — the highest in the five-year series. On a 4,072.50km B/C 
              network, that is roughly 285km of road flagged for maintenance by the council's own surveys.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (5,025.83km — 50.5% of network): back to the 2020 peak</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">69%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">22%</td>
                    <td className="px-4 py-2 text-center">72%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7%</td>
                    <td className="px-4 py-2 text-center font-bold">23%</td>
                    <td className="px-4 py-2 text-center font-bold">70%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED is back at 7% — matching the worst year of the series — on the road class that makes 
              up half the county. That is roughly 352km of residential streets, estate roads and rural 
              lanes flagged for maintenance.
            </p>
          </div>

          {/* Summary callout */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Around 686km of RED Road — And Rising Everywhere</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-rose-400">~49km</div>
                <div className="text-brand-300 text-sm">A roads in RED condition (6% of 819.68km)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">~285km</div>
                <div className="text-brand-300 text-sm">B/C roads in RED condition (7% of 4,072.50km)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">~352km</div>
                <div className="text-brand-300 text-sm">U roads in RED condition (7% of 5,025.83km)</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              In 2024, every road class in Norfolk hit its <span className="text-rose-400">highest RED 
              percentage of the five-year series</span>. The council's own definition of RED: "should be 
              considered for maintenance." The DfT's verdict on the whole programme: AMBER, on every scorecard.
            </p>
          </div>
        </div>
      </section>

      {/* The Four-Year Survey Cycle */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Four-Year Blind Spot
          </h2>
          <p className="text-surface-600 mb-8">
            Norfolk's own report sets out exactly how often each road class is condition-surveyed — 
            and for most of the network, the answer is once every four years
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Survey cycle (council's own description)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center">819.68km</td>
                  <td className="px-6 py-4 text-center">Surveyed yearly in one direction — both directions covered every 2 years</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">B roads</td>
                  <td className="px-6 py-4 text-center" rowSpan={2}>4,072.50km (combined)</td>
                  <td className="px-6 py-4 text-center">Surveyed yearly in one direction — both directions covered every 2 years</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">C roads</td>
                  <td className="px-6 py-4 text-center font-medium text-rose-700">50% surveyed per year, one direction — both directions covered every 4 years</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">U roads</td>
                  <td className="px-6 py-4 text-center font-bold">5,025.83km</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-700">25% surveyed per year — all U roads surveyed over a four-year period</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Half The County, Once Every Four Years</h3>
              <p className="text-surface-600 text-sm mb-3">
                Norfolk's report states it plainly: "We survey 25% of U roads each year. This means 
                that all U roads will be surveyed over a four-year period." U roads are 5,025.83km — 
                50.5% of the entire network.
              </p>
              <p className="text-surface-600 text-sm">
                A pothole that forms on a U road the month after its survey can, in principle, wait 
                <strong> nearly four years</strong> before the next network-level condition check. 
                In between, the council is relying on routine safety inspections and public reports 
                to find it.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">C Roads: The Same Story</h3>
              <p className="text-amber-800 text-sm mb-3">
                C roads — the rural routes that criss-cross a largely rural county — are only surveyed 
                in both directions every four years. The council's own words: "This means all 'C' roads 
                will have been surveyed in both directions every 4 years."
              </p>
              <p className="text-amber-800 text-sm">
                Norfolk is also switching survey methodology: SCANNER today, the new PAS 2161 national 
                standard from 2026/27. As other councils' reports concede, methodology changes make 
                before-and-after condition comparisons difficult.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence under the Highways Act 1980, a council must show it had 
              a reasonable system for knowing and maintaining the condition of the road that damaged 
              your vehicle. For most of Norfolk's network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — this year, or three years ago?</li>
              <li>• If 7% of U roads were RED at the last survey, what was done about yours?</li>
              <li>• How does the council track deterioration on a road it measures once every four years?</li>
              <li>• With RED percentages rising in every class, was the inspection frequency increased anywhere?</li>
            </ul>
            <p className="text-white font-medium">
              A four-year survey cycle on half the network places enormous weight on reactive reporting. 
              If the defect that damaged your vehicle had been reported before — by anyone — the council 
              had actual notice, and the Section 58 defence starts to crumble.
            </p>
          </div>
        </div>
      </section>

      {/* Potholes and spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            37,415 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Norfolk's own pothole counts swing sharply year to year — the council attributes the 
            spikes to winter weather
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change on previous year</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">5,516</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">8,244</td>
                  <td className="px-6 py-4 text-center text-rose-600">+49%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">6,589</td>
                  <td className="px-6 py-4 text-center text-emerald-600">−20%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">9,246</td>
                  <td className="px-6 py-4 text-center text-rose-600">+40%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">7,820</td>
                  <td className="px-6 py-4 text-center text-emerald-600">−15%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">37,415</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Weather Admission</h3>
              <p className="text-surface-600 text-sm">
                The report concedes that "Extreme weather conditions cause pothole numbers to increase 
                and there is a need to repair or make safe." Translation: after freeze-thaw winters, 
                defects form in surges — faster than any fixed survey cycle anticipates. The +49% and 
                +40% spikes in the table are exactly that pattern. If you hit a pothole in winter or 
                early spring, the question is how quickly the council's system caught up with the surge.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">A Modest Count For A Huge Network</h3>
              <p className="text-amber-800 text-sm">
                7,820 potholes filled in 2024/25 works out at under one repair per kilometre of road 
                per year, on a network where the council's own surveys flag roughly 686km as RED. 
                Compare councils with networks a third of the size repairing three times as many 
                defects. Either Norfolk's roads produce remarkably few potholes — or fewer of them 
                are being found and logged between four-yearly surveys.
              </p>
            </div>
          </div>

          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Spending Picture — And Why It's Still AMBER
          </h2>
          <p className="text-surface-600 mb-8">
            Norfolk has outspent its DfT capital allocation in every one of the last five reported years
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Capital spend</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Preventative / reactive</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">£54.2m</td>
                  <td className="px-6 py-4 text-center">£55.3m</td>
                  <td className="px-6 py-4 text-center">£15.9m</td>
                  <td className="px-6 py-4 text-center">96.0% / 4.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">£38.6m</td>
                  <td className="px-6 py-4 text-center">£45.9m</td>
                  <td className="px-6 py-4 text-center">£16.6m</td>
                  <td className="px-6 py-4 text-center">93.3% / 6.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">£38.0m</td>
                  <td className="px-6 py-4 text-center">£44.8m</td>
                  <td className="px-6 py-4 text-center">£19.2m</td>
                  <td className="px-6 py-4 text-center">93.2% / 6.8%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">£44.9m</td>
                  <td className="px-6 py-4 text-center">£49.3m</td>
                  <td className="px-6 py-4 text-center">£19.2m</td>
                  <td className="px-6 py-4 text-center">93.3% / 6.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">£42.0m</td>
                  <td className="px-6 py-4 text-center font-bold">£57.8m</td>
                  <td className="px-6 py-4 text-center">£18.0m</td>
                  <td className="px-6 py-4 text-center">93.5% / 6.5%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold">£56.1m</td>
                  <td className="px-6 py-4 text-center font-bold">£60.8m</td>
                  <td className="px-6 py-4 text-center font-bold">£21.3m</td>
                  <td className="px-6 py-4 text-center font-bold">95.7% / 4.3%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Outspending The Allocation — Still AMBER on Spend</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-amber-400">£57.8m</div>
                <div className="text-brand-300 text-sm">Capital spend 2024/25 — 38% above the £42.0m DfT allocation</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">4.3%</div>
                <div className="text-brand-300 text-sm">Projected share of spend on reactive maintenance in 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">AMBER</div>
                <div className="text-brand-300 text-sm">The DfT's verdict on Norfolk's spend — despite the headline numbers</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Norfolk spends above its allocation every year, and the DfT still rated its Spend 
              scorecard <span className="text-amber-400">AMBER</span>. Whatever the reason, the lesson 
              for claimants is simple: aggregate budget figures are not a Section 58 defence. The law 
              asks what was done about <span className="text-rose-400">your road</span>.
            </p>
          </div>
        </div>
      </section>

      {/* The surface dressing economy */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            4.26km of Resurfacing on a 9,944km Network
          </h2>
          <p className="text-surface-600 mb-8">
            What "over 500km treated" actually means when you read the activity tables
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Treatment in 2024/25</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">A roads</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">B roads</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">C roads</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U roads</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Surface dressing</td>
                  <td className="px-6 py-4 text-center font-bold">42.93km</td>
                  <td className="px-6 py-4 text-center font-bold">24.86km</td>
                  <td className="px-6 py-4 text-center font-bold">224.98km</td>
                  <td className="px-6 py-4 text-center font-bold">181.79km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Resurfacing</td>
                  <td className="px-6 py-4 text-center">3.64km</td>
                  <td className="px-6 py-4 text-center">0.48km</td>
                  <td className="px-6 py-4 text-center">0.14km</td>
                  <td className="px-6 py-4 text-center text-surface-400">0km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Overlay</td>
                  <td className="px-6 py-4 text-center">1.30km</td>
                  <td className="px-6 py-4 text-center">1.76km</td>
                  <td className="px-6 py-4 text-center text-surface-400">0km</td>
                  <td className="px-6 py-4 text-center text-surface-400">0km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Reconstruction</td>
                  <td className="px-6 py-4 text-center">2.41km</td>
                  <td className="px-6 py-4 text-center">2.40km</td>
                  <td className="px-6 py-4 text-center">1.95km</td>
                  <td className="px-6 py-4 text-center">0.65km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">In-situ recycling</td>
                  <td className="px-6 py-4 text-center text-surface-400">0km</td>
                  <td className="px-6 py-4 text-center">0.61km</td>
                  <td className="px-6 py-4 text-center">0.70km</td>
                  <td className="px-6 py-4 text-center">4.59km</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total treated</td>
                  <td className="px-6 py-4 text-center font-bold">57km</td>
                  <td className="px-6 py-4 text-center font-bold">30km</td>
                  <td className="px-6 py-4 text-center font-bold">228km</td>
                  <td className="px-6 py-4 text-center font-bold">187km</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">94.5% Surface Dressing</h3>
              <p className="text-surface-600 text-sm mb-3">
                Of the 502km treated in 2024/25, 474.56km — 94.5% — was surface dressing: a thin 
                layer of bitumen and chippings that seals a road surface in reasonable condition. 
                Conventional resurfacing totalled <strong>4.26km county-wide</strong>, and not a single 
                metre of it on the 5,025.83km unclassified network.
              </p>
              <p className="text-surface-600 text-sm">
                Surface dressing is legitimate, cost-effective asset management — the council's report 
                is right about that. But it is a preventative seal, not a repair for roads already 
                rated RED.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Arithmetic Problem</h3>
              <p className="text-amber-800 text-sm mb-3">
                Norfolk's own surveys flag roughly 686km of road as RED — "should be considered for 
                maintenance". Structural treatments (resurfacing, overlay, reconstruction, recycling) 
                covered about 20.6km in 2024/25.
              </p>
              <p className="text-amber-800 text-sm">
                At that rate, the RED backlog alone represents decades of structural work — and the 
                2024 condition data shows the backlog growing in every road class, not shrinking. 
                Treated kilometres also covered just 5% of the network in total.
              </p>
            </div>
          </div>

          <QuoteCard 
            quote="Our surface dressing programme is designed to help roads last a lot longer without potholes appearing and means that less is spent on reactive maintenance."
            source="Norfolk County Council, Local Highways Maintenance Transparency Report, June 2025"
          />
        </div>
      </section>

      {/* The admissions */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            In The Council's Own Words
          </h2>
          <p className="text-surface-600 mb-8">
            The transparency report contains several statements a claimant should know about
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="Norfolk County Council manages a large road network with limited funding. Our strategy is to keep roads safe and serviceable in the most efficient way possible."
              source="Norfolk County Council, Local Highways Maintenance Transparency Report, June 2025"
            />
            <QuoteCard 
              quote="Although we take a 'prevention is better than cure' approach, some reactive maintenance is inevitable. Extreme weather conditions cause pothole numbers to increase and there is a need to repair or make safe."
              source="Norfolk County Council, Local Highways Maintenance Transparency Report, June 2025"
            />
            <QuoteCard 
              quote="We survey 25% of U roads each year. This means that all U roads will be surveyed over a four-year period."
              source="Norfolk County Council, Local Highways Maintenance Transparency Report, June 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What These Admissions Mean</h3>
              <p className="text-surface-600 text-sm mb-3">
                "Limited funding" plus a four-year survey cycle on half the network plus an admission 
                that potholes surge with the weather — together, these define the gap a claim can live 
                in. The council knows defects form in winter surges; its condition surveys cannot catch 
                them on most roads for years at a time.
              </p>
              <p className="text-surface-600 text-sm">
                That places the weight of the Section 58 defence on routine safety inspections and 
                public reports — both of which can be tested with evidence requests for your specific road.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">One Careful Distinction</h3>
              <p className="text-amber-800 text-sm mb-3">
                Norfolk's report mentions "Section 58" — but that is Section 58 of the <em>New Roads 
                and Street Works Act 1991</em>, which protects freshly resurfaced streets from being 
                dug up by utilities for two years.
              </p>
              <p className="text-amber-800 text-sm">
                It is <strong>not</strong> the Section 58 defence under the <em>Highways Act 1980</em> that 
                the council will raise against your pothole claim. Don't confuse the two — and don't let 
                a claims handler blur them either.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Balanced assessment */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an All-AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Norfolk runs a coherent, well-documented preventative strategy — 
            and the DfT still couldn't give it a single GREEN
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Capital spend above the DfT allocation in every reported year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A documented whole-life-cost asset management strategy with defined road hierarchies
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A and B roads condition-surveyed every year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A Future Highway Research Group peer review calling it "a highly performing, innovative service"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Green percentages broadly stable or improving on B/C and U roads over five years
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a competent, paper-backed Section 58 defence. Generic template claims will struggle here.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER on all four DfT scorecards — no GREEN anywhere, including Best Practice
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED-rated roads at a five-year high in every road class in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Half the network (5,025.83km of U roads) surveyed once every four years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 4.26km of conventional resurfacing in 2024/25 — none on unclassified roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Admitted weather-driven pothole surges (+49% and +40% in single years)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Reactive maintenance squeezed to a projected 4.3% of spend
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is The Survey Gap</h3>
            <p className="text-brand-200 mb-4">
              Against a council whose paperwork is genuinely good, your claim turns on the 
              <strong className="text-white"> specific defect and the specific road</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — actual notice beats any inspection schedule</li>
              <li>• The road's class — on a C or U road, the four-year survey cycle is your strongest structural argument</li>
              <li>• Photos showing size, depth and visible age (weathered edges, previous patching, standing water)</li>
              <li>• Timing — a winter or early-spring incident sits squarely in the weather-surge pattern the council itself describes</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, 
              and cites Norfolk's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Norfolk?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-documented council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No four-year survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No road-class analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ All-AMBER DfT scorecard cited</li>
                <li>✅ Four-year U-road survey cycle argued</li>
                <li>✅ Five-year-high RED condition data documented</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Norfolk</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=norfolk"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Norfolk Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Norfolk is AMBER on all four DfT scorecards — what does that mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Norfolk received AMBER for Overall, Condition, Spend and Best Practice in the DfT's 
                2025/26 local road maintenance ratings — no GREEN anywhere. AMBER across the board means 
                the DfT sees room for improvement in every area it measures. For your claim, the most 
                relevant facts sit underneath the ratings: RED-rated roads hit a five-year high in every 
                road class in 2024, and half the network is condition-surveyed only once every four years.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a C road or an unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                That is where Norfolk's survey regime is thinnest. The council's own report says it 
                surveys 25% of U roads each year, so all U roads are surveyed over a four-year period — 
                and C roads are only covered in both directions every four years. U roads alone are 
                5,025.83km, just over half the network. A defect on one of these roads can sit a long 
                time between condition surveys, which makes prior reports and photographic evidence decisive.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Norfolk spends more than its DfT allocation — doesn't that sink my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Norfolk's capital spend has exceeded its DfT allocation in each of the last five 
                reported years, yet the DfT still rated its Spend scorecard AMBER. More importantly, 
                Section 58 of the Highways Act 1980 is not a budget defence — the council must show the 
                specific stretch of road that damaged your vehicle was reasonably inspected and maintained. 
                Aggregate spending does not answer that question.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Over 95% of Norfolk's spend is preventative — does that hurt my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily. The preventative-first approach means just 4.3-6.8% of spend goes on 
                reactive repairs, and in 2024/25 the dominant treatment was surface dressing — 474.56km 
                of it, against only 4.26km of conventional resurfacing across a 9,943.79km network. 
                Surface dressing seals roads in fair condition; it does not rebuild roads already rated 
                RED. With RED percentages at a five-year high in every class, the question for Section 58 
                is what was done about the roads the council's own data flags as needing maintenance.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How many potholes does Norfolk actually fix?
              </h3>
              <p className="text-surface-600 text-sm">
                Norfolk's report records 37,415 potholes filled over five years: 5,516 in 2020/21, 
                8,244 in 2021/22, 6,589 in 2022/23, 9,246 in 2023/24 and 7,820 in 2024/25. The council 
                says extreme weather drives the spikes. The fluctuating numbers — up 40% in 2023/24, 
                down 15% the next year — support what the report itself acknowledges: pothole numbers 
                surge after hard winters, faster than a fixed inspection cycle anticipates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.norfolk.gov.uk/article/70419/Norfolk-highways-maintenance-transparency-report-202526"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Norfolk County Council Local Highways Maintenance Transparency Report (June 2025)
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
